import { useEffect, useMemo } from 'react';
import { SquareTerminal } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { dismissJobRun, retryFailedJobSteps } from '@/api/generated/jobs/jobs';
import { Badge, Button, Skeleton, WindowContent } from '@/shared/ui';
import { useAgendaParams } from '@/shared/navigation/react/useAgendaParams';
import { useNavigationContext } from '@/shared/navigation/react/useNavigationContext';
import { JobSegmentedBar } from '../internal/JobSegmentedBar';
import { formatDuration, formatThousands } from '../internal/jobsFormat';
import {
  buildTreeRows,
  countsDone,
  leavesInDocumentOrder,
  runVisualStatus,
  segmentsFromCounts,
  segmentsFromLeaves,
  type JobVisualStatus,
} from '../internal/jobsModel';
import { useNow } from '../internal/useNow';
import { getJobResultActions, toJobResultActionContext } from '../jobResultActions';
import { useJobsStore } from '../jobsStore';
import { jobDetailAgenda } from './jobDetailAgenda';
import { JobTreeRow } from './components/JobTreeRow';

const BADGE_TONE: Record<JobVisualStatus, 'neutral' | 'info' | 'success' | 'danger'> = {
  queued: 'neutral',
  running: 'info',
  done: 'success',
  error: 'danger',
};

/**
 * Okno detailu jobu (dle designu): hlavička s titulkem a stavem, blok „Celkový průběh"
 * (segmentovaný bar + chipy počtů), strom kroků a patička se stavovým textem, výsledkovými
 * akcemi, „Opakovat chybné kroky" a „Zavřít". Data drží jobs store (SSE delty + fetch detailu).
 */
export function JobDetailPage({ layerId }: { layerId: string }) {
  const { t } = useTranslation('jobs');
  const { kernel } = useNavigationContext();
  const { open } = useAgendaParams(jobDetailAgenda, layerId);
  const runId = open.runId;
  const now = useNow();

  const entry = useJobsStore((s) => (runId ? s.runs[runId] : undefined));
  const collapseOverrides = useJobsStore((s) => s.collapseOverrides);
  const toggleCollapsed = useJobsStore((s) => s.toggleCollapsed);
  const loadDetail = useJobsStore((s) => s.loadDetail);

  useEffect(() => {
    if (runId && !useJobsStore.getState().runs[runId]?.detailLoaded) {
      void loadDetail(runId);
    }
  }, [runId, loadDetail]);

  const steps = useMemo(() => (entry?.steps ? Object.values(entry.steps) : []), [entry?.steps]);
  const rows = useMemo(
    () => (runId ? buildTreeRows(runId, steps, collapseOverrides) : []),
    [runId, steps, collapseOverrides]
  );
  const leaves = useMemo(() => leavesInDocumentOrder(steps), [steps]);

  if (!runId) return null;

  const run = entry?.summary;
  if (!run || (!entry.detailLoaded && entry.detailLoading)) {
    return (
      <WindowContent data-testid="job-detail-loading">
        <div className="gap-phx-3 flex flex-col">
          <Skeleton className="h-16" />
          <Skeleton className="h-10" />
          <Skeleton className="h-40" />
        </div>
      </WindowContent>
    );
  }

  if (!entry.detailLoaded) {
    return (
      <WindowContent data-testid="job-detail-error">
        <div className="text-phx-text-muted text-[13px]">{t('detail.loadFailed')}</div>
      </WindowContent>
    );
  }

  const visual = runVisualStatus(run);
  const counts = run.counts;
  const done = countsDone(counts);
  const durationMs =
    (run.completedAt ? Date.parse(run.completedAt) : now) - Date.parse(run.createdAt);
  const overallSegments =
    leaves.length > 0 ? segmentsFromLeaves(leaves) : segmentsFromCounts(counts);
  const actions = run.status === 'Succeeded' ? getJobResultActions(run.jobType) : [];
  const footText =
    counts.failed > 0
      ? t('detail.foot.error')
      : visual === 'done'
        ? t('detail.foot.done')
        : t('detail.foot.running');

  return (
    <WindowContent scroll={false} className="p-0" data-testid="job-detail">
      <div className="flex h-full min-h-0 flex-col">
        {/* Hlavička: dlaždice s ikonou, titulek + stavový badge, meta řádek. */}
        <div className="border-phx-border-2 bg-phx-surface-2 flex shrink-0 items-start gap-[11px] border-b py-3 pr-3 pl-4">
          <span className="bg-phx-primary-tint text-phx-primary rounded-phx-md inline-flex h-[30px] w-[30px] shrink-0 items-center justify-center">
            <SquareTerminal size={17} aria-hidden />
          </span>
          <span className="flex min-w-0 flex-1 flex-col gap-0.5">
            <span className="flex items-start gap-2">
              <span className="text-phx-text min-w-0 flex-1 text-[14px] leading-[1.4] font-semibold">
                {run.title}
              </span>
              <Badge tone={BADGE_TONE[visual]} appearance="soft" className="mt-px shrink-0">
                {t(`status.${visual}`)}
              </Badge>
            </span>
            <span className="text-phx-text-muted text-[11.5px]">
              {t('detail.sub', { count: counts.total, duration: formatDuration(durationMs) })}
              {' · '}
              {t('detail.subInitiator', { name: run.initiatedByUserName })}
            </span>
          </span>
        </div>

        {/* Celkový průběh: bar + chipy počtů. */}
        <div className="border-phx-border mb-1.5 flex shrink-0 flex-col gap-[9px] border-b px-4 pt-[13px] pb-4">
          <div className="flex items-baseline justify-between">
            <span className="text-phx-text-muted text-[10px] font-semibold tracking-[.05em] uppercase">
              {t('detail.overall')}
            </span>
            <span className="text-phx-text-2 text-[13px]">
              <b className="text-phx-text text-[14px] tabular-nums">
                {formatThousands(done)} / {formatThousands(counts.total)}
              </b>{' '}
              {t('detail.doneOf')}
            </span>
          </div>
          <JobSegmentedBar segments={overallSegments} className="h-[7px] rounded-[4px]" />
          <div className="flex flex-wrap items-center gap-1.5">
            {counts.running > 0 ? (
              <Badge tone="info" appearance="soft">
                {t('detail.chips.running', { count: formatThousands(counts.running) })}
              </Badge>
            ) : null}
            {counts.blocked + counts.ready > 0 ? (
              <Badge tone="neutral" appearance="soft">
                {t('detail.chips.queued', {
                  count: formatThousands(counts.blocked + counts.ready),
                })}
              </Badge>
            ) : null}
            {done > 0 ? (
              <Badge tone="success" appearance="soft">
                {t('detail.chips.done', { count: formatThousands(done) })}
              </Badge>
            ) : null}
            {counts.failed > 0 ? (
              <Badge tone="danger" appearance="soft">
                {t('detail.chips.error', { count: counts.failed })}
              </Badge>
            ) : null}
          </div>
        </div>

        {/* Strom kroků. */}
        <div className="min-h-0 flex-1 overflow-x-hidden overflow-y-auto pb-1.5">
          {rows.map((row) => (
            <JobTreeRow
              key={row.key}
              row={row}
              runId={runId}
              now={now}
              onToggle={(group) =>
                toggleCollapsed(group.key, !group.collapsed, group.defaultCollapsed)
              }
            />
          ))}
        </div>

        {/* Patička: stavový text, výsledkové akce, opakování chyb, zavření. */}
        <div className="border-phx-border-2 bg-phx-surface-2 flex shrink-0 items-center gap-2 border-t px-3 py-[9px]">
          <span className="text-phx-text-muted text-[11.5px]">{footText}</span>
          <span className="flex-1" />
          {actions.map((action) => (
            <Button
              key={action.key}
              size="sm"
              className="shrink-0"
              onClick={() => action.onActivate(toJobResultActionContext(run, kernel))}
            >
              {t(action.labelKey)}
            </Button>
          ))}
          {counts.failed > 0 ? (
            <Button
              size="sm"
              className="shrink-0"
              onClick={() => void retryFailedJobSteps(runId).catch(() => undefined)}
            >
              {t('detail.retryFailed')}
            </Button>
          ) : null}
          {run.status === 'Failed' ? (
            <Button
              size="sm"
              className="shrink-0"
              onClick={() =>
                void dismissJobRun(runId)
                  .then(() => {
                    // Dismiss uvolní ConcurrencyKey a schová běh z „mých jobů" — ukliď i session stav.
                    useJobsStore.getState().removeRun(runId);
                    void kernel.closeLayer(layerId);
                  })
                  .catch(() => undefined)
              }
            >
              {t('detail.dismiss')}
            </Button>
          ) : null}
          <Button size="sm" variant="primary" onClick={() => void kernel.closeLayer(layerId)}>
            {t('detail.close')}
          </Button>
        </div>
      </div>
    </WindowContent>
  );
}
